<template>
  <LoadingIndicator v-if="loading" message="Loading schedule..." />
  <div v-else-if="loadError" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ loadError }}</div>
  <div v-else class="space-y-6">
    <HermesRecordHeader :back="{ name: 'AdminHermesSchedules' }" back-label="All schedules" :title="isNew ? 'New schedule' : `${schedule.template_name} · ${schedule.executor_name}`" :meta="isNew ? '' : `Schedule ${schedule.id} · created ${absolute(schedule.created_at)}`">
      <template v-if="!isNew && !editing" #actions>
        <button type="button" :class="TEXT_BUTTON" @click="editing = true">Edit</button>
        <button type="button" :class="TEXT_BUTTON" @click="confirmDelete = true">Delete</button>
        <button type="button" :class="SECONDARY_BUTTON" :disabled="running || runBusy" :title="running ? 'A run from this schedule is still going.' : ''" @click="runNow">Run now</button>
      </template>
    </HermesRecordHeader>
    <div v-if="confirmDelete" class="rounded-md bg-neutral-50 px-4 py-3 text-gray-900 flex items-center gap-3">
      <span>Delete this schedule? Its past runs are kept; no new runs start.</span>
      <button type="button" :class="[TEXT_BUTTON, 'ml-auto']" @click="confirmDelete = false">Keep it</button>
      <button type="button" :class="PRIMARY_BUTTON" @click="remove">Delete schedule</button>
    </div>
    <div v-if="actionError" role="alert" class="rounded-md bg-red-tint px-4 py-3 text-gray-900">{{ actionError }}</div>

    <div class="grid grid-cols-12 gap-6 items-start">
      <div class="col-span-7 space-y-6">
        <HermesCard :title="editing ? (isNew ? 'New schedule' : 'Edit schedule') : 'Schedule'">
          <template v-if="editing">
            <section class="mb-6">
              <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Template</h3>
              <label
v-for="t in templates" :key="t.id"
                     :class="['flex gap-3 rounded-md border p-3 mb-2', !t.population ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer', form.template === t.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200']">
                <input v-model="form.template" type="radio" :value="t.id" :disabled="!t.population" class="mt-1">
                <span>
                  <span class="block font-medium text-gray-900">{{ t.name }}</span>
                  <span class="block text-gray-600">v{{ t.version }} · {{ t.unit }} · {{ t.inputs.length }} inputs · effects {{ t.effects.length ? t.effects.join(', ') : 'none' }}</span>
                  <span v-if="!t.population" class="block text-gray-500">No population of its own, so a schedule would have no one to run on.</span>
                </span>
              </label>
            </section>
            <section class="mb-6">
              <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Executor</h3>
              <label
v-for="e in executors" :key="e.id"
                     :class="['flex gap-3 rounded-md border p-3 mb-2', e.identity === 'requester' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer', form.executor === e.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200']">
                <input v-model="form.executor" type="radio" :value="e.id" :disabled="e.identity === 'requester'" class="mt-1">
                <span>
                  <span class="block font-medium text-gray-900">{{ e.name }}</span>
                  <span class="block text-gray-600">{{ words(e.runtime) }} · {{ e.model }}{{ e.reasoning ? ` · ${e.reasoning}` : '' }}</span>
                  <span v-if="e.identity === 'requester'" class="block text-gray-500">Acts as its requester; a scheduled run has no requesting person.</span>
                </span>
              </label>
            </section>
            <section v-if="template" class="mb-6">
              <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Run parameters</h3>
              <label v-for="p in template.params" :key="p.name" class="block mb-3">
                <span class="block text-gray-500 mb-2"><span class="text-gray-900">{{ p.name }}</span> · {{ p.required ? 'Required' : 'Optional' }}</span>
                <input v-model="params[p.name]" :class="INPUT">
              </label>
              <label class="block">
                <span class="block text-gray-500 mb-2"><span class="text-gray-900">sample</span> · Optional; leave empty to run the whole population</span>
                <input v-model.number="sample" type="number" min="1" :class="[SIZED_INPUT, 'w-40']">
              </label>
            </section>
            <section class="mb-6">
              <h3 class="text-[11px] font-medium uppercase tracking-wider text-gray-500 mb-2">Cadence</h3>
              <div class="inline-flex rounded-md border border-gray-300 overflow-hidden">
                <button v-for="c in ['daily', 'weekly', 'monthly']" :key="c" type="button" :class="['px-4 py-2', form.cadence === c ? 'bg-neutral-100 text-gray-900' : 'bg-white text-gray-600']" @click="form.cadence = c">{{ words(c) }}</button>
              </div>
              <p class="text-gray-500 mt-2">The first run starts on the next scheduler pass; each later run starts {{ intervalWords[form.cadence] }} after the previous one finished.</p>
            </section>
            <label class="inline-flex items-center gap-2"><input v-model="form.is_active" type="checkbox" class="rounded border-gray-300"> Active</label>
          </template>
          <dl v-else class="grid grid-cols-3 gap-y-3">
            <dt class="text-gray-500">Template</dt><dd class="col-span-2"><router-link :to="{ name: 'AdminHermesTemplate', params: { templateId: schedule.template } }" class="text-neutral-800">{{ schedule.template_name }}</router-link> <span class="text-gray-500">runs the latest version (v{{ template && template.version }})</span></dd>
            <dt class="text-gray-500">Executor</dt><dd class="col-span-2"><router-link :to="{ name: 'AdminHermesExecutor', params: { executorKey: schedule.executor_key } }" class="text-neutral-800">{{ schedule.executor_name }}</router-link></dd>
            <dt class="text-gray-500">Params</dt><dd class="col-span-2"><span v-for="(v, k) in schedule.params" :key="k" class="inline-block rounded bg-gray-100 px-2 mr-1">{{ k }} {{ v }}</span><span v-if="!Object.keys(schedule.params || {}).length" class="text-gray-500">Full population</span></dd>
            <dt class="text-gray-500">Cadence</dt><dd class="col-span-2">{{ words(schedule.cadence) }} · {{ intervalWords[schedule.cadence] }}</dd>
            <dt class="text-gray-500">Status</dt><dd class="col-span-2"><HermesBadge :value="schedule.is_active ? 'active' : 'inactive'" :label="schedule.is_active ? 'Active' : 'Paused'" /></dd>
            <dt class="text-gray-500">Last 10 runs</dt><dd class="col-span-2 tabular-nums">{{ history.filter((r) => r.status === 'completed').length }} of {{ history.length }} completed · {{ money(history.reduce((a, r) => a + ((r.usage || {}).cost_usd || 0), 0)) }} total</dd>
          </dl>
        </HermesCard>
      </div>

      <div class="col-span-5 space-y-6">
        <HermesCard title="What this schedule may do unattended">
          <p v-if="!executor || !template" class="text-gray-600">Choose a template and an executor.</p>
          <template v-else>
            <p class="text-gray-700 mb-3">Runs act as <span class="sensitive">{{ executor.service_user_name || 'no service user' }}</span>, the {{ executor.name }} service user, with its grants.</p>
            <p v-if="!template.effects.length" class="text-gray-600">{{ template.name }} declares no effects, so each run produces results and frames only.</p>
            <table v-else class="w-full table-fixed">
              <colgroup><col style="width:30%"><col style="width:25%"><col style="width:45%"></colgroup>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="k in template.effects" :key="k">
                  <td :class="['py-2 pr-2', !grantFor(k) ? 'border-l-2 border-primary-500 pl-2' : '']">{{ k }}</td>
                  <td class="py-2 pr-2"><HermesBadge :value="grantFor(k) || 'failed'" :label="grantFor(k) === 'direct' ? 'Direct' : (grantFor(k) === 'propose' ? 'Propose' : 'Not allowed')" /></td>
                  <td class="py-2 text-gray-700">{{ grantFor(k) === 'direct' ? 'Written during the run, unreviewed.' : (grantFor(k) === 'propose' ? 'Waits in Approvals for a person.' : 'The executor cannot make this write.') }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </HermesCard>
        <HermesCard v-if="editing && estimate" title="Cost estimate">
          <p class="text-gray-700">{{ estimate }}</p>
        </HermesCard>
      </div>
    </div>

    <div v-if="editing" class="sticky bottom-0 z-10 -mx-8 border-t border-gray-200 bg-white px-8 py-3 flex items-center gap-4">
      <div v-if="blocker || saveError" role="alert" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ blocker || saveError }}</div>
      <div class="ml-auto flex gap-3">
        <button type="button" :class="TEXT_BUTTON" @click="isNew ? $router.push({ name: 'AdminHermesSchedules' }) : (editing = false)">Cancel</button>
        <button type="button" :class="PRIMARY_BUTTON" :disabled="!!blocker || !form.template || !form.executor || saving" @click="save">{{ saving ? 'Saving…' : 'Save schedule' }}</button>
      </div>
    </div>

    <HermesCard v-if="!isNew && !editing" title="Run history" meta="Last 10 runs" collapsible flush>
      <template #actions><router-link :to="{ name: 'AdminHermesRuns', query: { template: schedule.template, executor: schedule.executor } }" :class="TEXT_BUTTON">All runs of this pairing</router-link></template>
      <table class="w-full table-fixed divide-y divide-gray-200">
        <colgroup><col style="width:9%"><col style="width:16%"><col style="width:13%"><col style="width:10%"><col style="width:16%"><col style="width:10%"><col style="width:12%"><col style="width:14%"></colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH">Run</th><th :class="TH">Started</th><th :class="TH">Status</th><th :class="[TH, 'text-right']">Results</th>
          <th :class="TH">Units</th><th :class="[TH, 'text-right']">Cost</th><th :class="TH">Duration</th><th :class="TH">Version</th>
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <tr v-if="!history.length"><td colspan="8" class="px-3 py-8 text-center text-gray-500">This schedule has not run yet.</td></tr>
          <template v-for="r in history" :key="r.id">
            <tr>
              <td :class="[TD, r.status === 'failed' ? 'border-l-2 border-primary-500' : '']"><router-link :to="{ name: 'AdminHermesRun', params: { runId: r.id } }" class="text-neutral-800">#{{ r.id }}</router-link></td>
              <td :class="TD" :title="absolute(r.created_at)">{{ relative(r.created_at) }}</td>
              <td :class="TD"><HermesBadge :value="r.status" /></td>
              <td :class="[TD, 'text-right tabular-nums']">{{ r.results }}</td>
              <td :class="[TD,' tabular-nums']">{{ progressLine(r) }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ money((r.usage || {}).cost_usd) }}</td>
              <td :class="[TD, 'tabular-nums']">{{ duration(r.started_at, r.finished_at) }}</td>
              <td :class="TD">v{{ r.template_version }}</td>
            </tr>
            <tr v-if="r.status === 'failed' && r.error"><td colspan="8" class="px-3 py-2 bg-red-tint text-gray-900">{{ r.error }}</td></tr>
          </template>
        </tbody>
      </table>
    </HermesCard>
  </div>
</template>

<script>
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import HermesBadge from './HermesBadge.vue';
import HermesCard from './HermesCard.vue';
import HermesRecordHeader from './HermesRecordHeader.vue';
import { API, INPUT, SIZED_INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH, absolute, duration, errorOf, money, progressLine, relative, words,  } from './hermes';

export default {
  name: 'HermesScheduleEditor',
  components: { LoadingIndicator, HermesBadge, HermesCard, HermesRecordHeader },
  data() {
    return {
      loading: true, loadError: '', saving: false, saveError: '', actionError: '', runBusy: false, confirmDelete: false,
      schedule: {}, templates: [], executors: [], history: [], lastRun: null, editing: false,
      form: { template: null, executor: null, cadence: 'weekly', is_active: true }, params: {}, sample: null,
      intervalWords: { daily: 'every 20 hours', weekly: 'every 7 days', monthly: 'every 30 days' },
      INPUT, SIZED_INPUT, PRIMARY_BUTTON, SECONDARY_BUTTON, TD, TEXT_BUTTON, TH,
    };
  },
  computed: {
    isNew() {
      return this.$route.params.scheduleId === 'new';
    },
    template() {
      return this.templates.find((t) => t.id === (this.editing ? this.form.template : this.schedule.template)) || null;
    },
    executor() {
      return this.executors.find((e) => e.id === (this.editing ? this.form.executor : this.schedule.executor)) || null;
    },
    running() {
      return this.history.some((r) => ['pending', 'running'].includes(r.status));
    },
    missingGrants() {
      if (!this.template || !this.executor) return [];
      return this.template.effects.filter((k) => !this.grantFor(k));
    },
    blocker() {
      if (!this.editing || !this.missingGrants.length) return '';
      return `${this.executor.name} has no grant for ${this.missingGrants.join(' and ')}, which ${this.template.name} declares, so this schedule cannot be saved.`;
    },
    estimate() {
      const r = this.lastRun;
      if (!r || !(r.progress || {}).done || !(r.usage || {}).cost_usd) return '';
      const perUnit = r.usage.cost_usd / r.progress.done;
      const perMonth = { daily: 30, weekly: 30 / 7, monthly: 1 }[this.form.cadence];
      if (!this.sample) return `${money(perUnit)} per unit on run #${r.id}; a full-population run costs that times the population size, about ${perMonth.toFixed(1)} times a month.`;
      return `About ${money(perUnit * this.sample)} per run and ${money(perUnit * this.sample * perMonth)} a month, at ${money(perUnit)} per unit from run #${r.id}.`;
    },
  },
  watch: {
    'form.template'() {
      if (this.editing) this.loadLastRun();
    },
    'form.executor'() {
      if (this.editing) this.loadLastRun();
    },
    editing(on) {
      if (on && !this.isNew) this.fillForm();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    absolute, duration, money, progressLine, relative, words,
    async load() {
      try {
        const calls = [this.$axios.get(`${API}/templates/`), this.$axios.get(`${API}/executors/`)];
        if (!this.isNew) {
          const id = this.$route.params.scheduleId;
          calls.push(this.$axios.get(`${API}/schedules/${id}/`), this.$axios.get(`${API}/runs/`, { params: { schedule: id, limit: 10 } }));
        }
        const [t, e, s, h] = await Promise.all(calls);
        this.templates = t.data.filter((x) => x.is_active);
        this.executors = e.data.filter((x) => x.usable);
        if (this.isNew) {
          this.editing = true;
        } else {
          this.schedule = s.data;
          this.history = h.data.results;
        }
      } catch (err) {
        this.loadError = err?.response?.status === 404 ? 'No schedule with that id on this tenant.' : errorOf(err, 'The schedule could not be loaded.');
      } finally {
        this.loading = false;
      }
    },
    fillForm() {
      this.form = { template: this.schedule.template, executor: this.schedule.executor, cadence: this.schedule.cadence, is_active: this.schedule.is_active };
      const { sample, ...rest } = this.schedule.params || {};
      this.params = { ...rest };
      this.sample = sample || null;
    },
    grantFor(kind) {
      const g = (this.executor.grants || []).find((x) => x.kind === kind);
      return g ? g.mode : null;
    },
    async loadLastRun() {
      this.lastRun = null;
      if (!this.form.template || !this.form.executor) return;
      const { data } = await this.$axios.get(`${API}/runs/`, { params: { template: this.form.template, executor: this.form.executor, status: 'completed', limit: 1 } });
      this.lastRun = data.results[0] || null;
    },
    async save() {
      this.saving = true;
      this.saveError = '';
      const params = Object.fromEntries(Object.entries(this.params).filter(([, v]) => String(v ?? '').trim() !== ''));
      if (this.sample) params.sample = this.sample;
      const body = { ...this.form, params };
      try {
        if (this.isNew) {
          const { data } = await this.$axios.post(`${API}/schedules/`, body);
          this.$router.replace({ name: 'AdminHermesSchedule', params: { scheduleId: data.id } });
        } else {
          this.schedule = (await this.$axios.patch(`${API}/schedules/${this.schedule.id}/`, body)).data;
          this.editing = false;
        }
      } catch (e) {
        this.saveError = errorOf(e, 'The schedule was not saved.');
      } finally {
        this.saving = false;
      }
    },
    async runNow() {
      this.runBusy = true;
      this.actionError = '';
      try {
        const { data } = await this.$axios.post(`${API}/schedules/${this.schedule.id}/run/`);
        this.$router.push({ name: 'AdminHermesRun', params: { runId: data.id } });
      } catch (e) {
        this.actionError = errorOf(e, 'The run was not started.');
      } finally {
        this.runBusy = false;
      }
    },
    async remove() {
      try {
        await this.$axios.delete(`${API}/schedules/${this.schedule.id}/`);
        this.$router.push({ name: 'AdminHermesSchedules' });
      } catch (e) {
        this.actionError = errorOf(e, 'The schedule was not deleted.');
      }
    },
  },
};
</script>
